import { action, computed, makeAutoObservable, observable, runInAction } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { ToasterState } from 'src/AppState/ToasterState';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import {
    DepositLimitConfigCalculationTypeType,
    DepositLimitConfigLimitTypeType,
    DepositLimitConfigRequestType,
    DepositLimitConfigResponseType,
} from 'src/api/config/accountDepositLimits/depositLimitConfigSchemas';
import { ValueType } from 'src/domains/common/components/select/select.state';

export const DEFAULT_DEPOSIT_LIMIT_PERIOD_OPTIONS: Array<ValueType<string>> = [
    { value: '0', label: 'Lifetime' },
    { value: '1', label: '1 day' },
    { value: '7', label: '7 days' },
    { value: '30', label: '30 days' },
    { value: '90', label: '90 days' },
    { value: '365', label: '365 days' },
];

const CALENDAR_PERIOD_LABELS: Record<string, string> = {
    '1': 'Daily',
    '7': 'Weekly',
    '30': 'Monthly',
};

export const DEFAULT_DEPOSIT_LIMIT_CALCULATION_OPTIONS: Array<ValueType<string>> = [
    { value: 'net', label: 'Net deposit' },
    { value: 'gross', label: 'Gross deposit' },
];

export type DefaultDepositLimitGlobalType = Exclude<DepositLimitConfigLimitTypeType, 'lifetime'>;

export const DEFAULT_DEPOSIT_LIMIT_TYPE_OPTIONS: Array<ValueType<string>> = [
    { value: 'rolling', label: 'Rolling', dataTest: 'default-deposit-limits-type-option-rolling' },
    { value: 'calendar', label: 'Calendar', dataTest: 'default-deposit-limits-type-option-calendar' },
];

const PENCE_PER_POUND = 100;
const DEFAULT_EMAIL_PAUSE_DAYS = 30;
const EMAIL_WINDOW_DAYS = new Set([30, 90, 365]);

const validateDefaultAmount = (value: string): Result<number> => {
    const trimmed = value.trim();

    if (trimmed === '') {
        return Result.createError('This field is required.');
    }

    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
        return Result.createError('Enter a valid amount.');
    }

    const pounds = parseFloat(trimmed);

    if (isNaN(pounds) || pounds <= 0) {
        return Result.createError('Enter an amount greater than 0.');
    }

    return Result.createOk(Math.round(pounds * PENCE_PER_POUND));
};

const isAmountUnsetValue = (value: string): boolean => {
    const trimmed = value.trim();

    if (trimmed === '') {
        return true;
    }

    const pounds = parseFloat(trimmed);

    return isNaN(pounds) || pounds <= 0;
};

const CUSTOM_WINDOW_DAYS_MIN = 1;
const CUSTOM_WINDOW_DAYS_MAX = 366;

const validateCustomWindowDays = (value: string): Result<number> => {
    const trimmed = value.trim();

    if (trimmed === '') {
        return Result.createError('Enter number of days.');
    }

    if (!/^\d+$/.test(trimmed)) {
        return Result.createError('Enter a whole number of days.');
    }

    const days = parseInt(trimmed, 10);

    if (days < CUSTOM_WINDOW_DAYS_MIN || days > CUSTOM_WINDOW_DAYS_MAX) {
        return Result.createError(
            `Enter a number of days between ${CUSTOM_WINDOW_DAYS_MIN} and ${CUSTOM_WINDOW_DAYS_MAX}.`
        );
    }

    return Result.createOk(days);
};

const penceToPoundsString = (pence: number): string => (pence / PENCE_PER_POUND).toFixed(2);

const parseWindowDays = (value: string): number => parseInt(value, 10);

const STANDARD_WINDOW_DAYS = new Set(
    DEFAULT_DEPOSIT_LIMIT_PERIOD_OPTIONS.map((option) => parseWindowDays(option.value))
);

export const isCustomDepositLimitWindow = (windowDays: number): boolean =>
    STANDARD_WINDOW_DAYS.has(windowDays) === false;

const limitConfigKey = (windowDays: number, calculationType: DepositLimitConfigCalculationTypeType): string =>
    `${windowDays}:${calculationType}`;

export const periodLabel = (windowDays: number, limitType?: DefaultDepositLimitGlobalType): string => {
    if (limitType === 'calendar') {
        const calendarLabel = CALENDAR_PERIOD_LABELS[String(windowDays)];

        if (calendarLabel !== undefined) {
            return calendarLabel;
        }
    }

    return (
        DEFAULT_DEPOSIT_LIMIT_PERIOD_OPTIONS.find((option) => option.value === String(windowDays))?.label ??
        `${windowDays} days`
    );
};

const toCalculationType = (value: string): DepositLimitConfigCalculationTypeType => {
    return value === 'gross' ? 'gross' : 'net';
};

const toGlobalLimitType = (value: string): DefaultDepositLimitGlobalType => {
    return value === 'calendar' ? 'calendar' : 'rolling';
};

export class DefaultDepositLimitItemState {
    public readonly amount: FormInputState<string, number>;
    public readonly amountOver: FormInputState<string, number>;
    public readonly calculationType: FormInputState<string, string>;
    public readonly limitType: FormInputState<string, string>;
    @observable public limitEnabled: boolean;
    @observable public hardLimit: boolean;
    @observable public sendEmail: boolean;
    @observable public splitByAge: boolean;
    @observable public existing: DepositLimitConfigResponseType | null = null;

    public constructor(
        public readonly id: string,
        public readonly windowDays: number,
        amountPounds: string,
        amountOverPounds: string,
        calculationType: DepositLimitConfigCalculationTypeType,
        limitType: DefaultDepositLimitGlobalType,
        limitEnabled: boolean,
        hardLimit: boolean,
        sendEmail: boolean,
        splitByAge: boolean,
        existing: DepositLimitConfigResponseType | null
    ) {
        const calculationTypeValue: string = calculationType;
        const limitTypeValue: string = calculationType === 'gross' ? 'calendar' : limitType;
        this.amount = FormInputState.new(amountPounds).map(validateDefaultAmount);
        this.amountOver = FormInputState.new(amountOverPounds).map(validateDefaultAmount);
        this.calculationType = FormInputState.new(calculationTypeValue);
        this.limitType = FormInputState.new(limitTypeValue);
        this.limitEnabled = limitEnabled;
        this.hardLimit = hardLimit;
        this.sendEmail = sendEmail;
        this.splitByAge = splitByAge;
        this.existing = existing;
        makeAutoObservable(this);
    }

    @computed public get errors(): Array<string> {
        if (this.limitEnabled === false) {
            return [];
        }

        if (isAmountUnsetValue(this.amount.value) && this.amount.errorsForView.length > 0) {
            return ['Enter an amount to enable this limit.'];
        }

        return this.amount.errorsForView;
    }

    @computed public get amountOverErrors(): Array<string> {
        if (this.limitEnabled === false || this.splitByAge === false) {
            return [];
        }

        if (isAmountUnsetValue(this.amountOver.value) && this.amountOver.errorsForView.length > 0) {
            return ['Enter an amount to enable this limit.'];
        }

        return this.amountOver.errorsForView;
    }

    @computed public get hasError(): boolean {
        if (this.limitEnabled === false) {
            return false;
        }

        if (this.amount.result.value.type === 'error') {
            return true;
        }

        return this.splitByAge && this.amountOver.result.value.type === 'error';
    }

    @computed public get isCustomWindow(): boolean {
        return isCustomDepositLimitWindow(this.windowDays);
    }

    @computed public get calculationTypeValue(): DepositLimitConfigCalculationTypeType {
        if (this.isCustomWindow) {
            return 'net';
        }

        return toCalculationType(this.calculationType.value);
    }

    @computed public get displayLimitType(): DefaultDepositLimitGlobalType {
        if (this.isCustomWindow) {
            return 'rolling';
        }

        if (this.calculationTypeValue === 'gross') {
            return 'calendar';
        }

        return toGlobalLimitType(this.limitType.value);
    }

    @computed public get persistLimitType(): DepositLimitConfigLimitTypeType {
        if (this.windowDays === 0) {
            return 'lifetime';
        }

        return this.displayLimitType;
    }

    @computed public get isCalculationTypeLocked(): boolean {
        return this.isCustomWindow;
    }

    @computed public get isLimitTypeLocked(): boolean {
        return this.windowDays === 0 || this.calculationTypeValue === 'gross' || this.isCustomWindow;
    }

    @computed public get canSendEmail(): boolean {
        return this.persistLimitType === 'rolling' && EMAIL_WINDOW_DAYS.has(this.windowDays);
    }

    @action public setLimitTypeToCalendar = (): void => {
        this.limitType.setValue('calendar');
    };

    @action public setLimitEnabled = (limitEnabled: boolean): void => {
        this.limitEnabled = limitEnabled;

        if (limitEnabled) {
            if (isAmountUnsetValue(this.amount.value)) {
                this.amount.setValue('');
            }

            this.amount.setAsVisited();

            if (this.splitByAge) {
                if (isAmountUnsetValue(this.amountOver.value)) {
                    this.amountOver.setValue('');
                }

                this.amountOver.setAsVisited();
            }
        }
    };

    @action public setSplitByAge = (enabled: boolean): void => {
        if (enabled && this.amountOver.value.trim() === '') {
            this.amountOver.setValue(this.amount.value);
        }

        this.splitByAge = enabled;
    };

    @action public setHardLimit = (hardLimit: boolean): void => {
        this.hardLimit = hardLimit;
    };

    @action public setSendEmail = (sendEmail: boolean): void => {
        if (this.canSendEmail === false) {
            this.sendEmail = false;
            return;
        }

        this.sendEmail = sendEmail;
    };
}

export class DefaultDepositLimitsState {
    @observable public items: Array<DefaultDepositLimitItemState> = [];
    @observable public existingByWindowAndCalculation: Map<string, DepositLimitConfigResponseType> = new Map();
    @observable public isLoading = true;
    @observable public isSaving = false;
    @observable public customSplitByAge = false;
    @observable public hideInactiveLimits = true;
    @observable public errorMessage: string | null = null;
    @observable public addErrorMessage: string | null = null;

    public readonly customWindowDays: FormInputState<string, number>;
    public readonly customAmount: FormInputState<string, number>;
    public readonly customAmountOver: FormInputState<string, number>;

    public constructor(private readonly appState: AppState) {
        this.customWindowDays = FormInputState.new('').map(validateCustomWindowDays);
        this.customAmount = FormInputState.new('').map(validateDefaultAmount);
        this.customAmountOver = FormInputState.new('').map(validateDefaultAmount);
        makeAutoObservable(this);
        void this.loadConfiguration();
    }

    @computed public get universeLimitType(): DefaultDepositLimitGlobalType {
        return ConfigState.get(this.appState.common).config.depositLimitType === 'calendarType'
            ? 'calendar'
            : 'rolling';
    }

    @computed public get defaultNetDepositLimitTypeLabel(): 'CALENDAR' | 'ROLLING' {
        return this.universeLimitType === 'calendar' ? 'CALENDAR' : 'ROLLING';
    }

    @action public setCustomSplitByAge = (enabled: boolean): void => {
        if (enabled && this.customAmountOver.value.trim() === '') {
            this.customAmountOver.setValue(this.customAmount.value);
        }

        this.customSplitByAge = enabled;
    };

    @action public setHideInactiveLimits = (hideInactiveLimits: boolean): void => {
        this.hideInactiveLimits = hideInactiveLimits;
    };

    @computed public get visibleItems(): Array<DefaultDepositLimitItemState> {
        const items = this.hideInactiveLimits ? this.items.filter((item) => item.limitEnabled) : this.items;

        return items.slice().sort((left, right) => {
            if (left.windowDays !== right.windowDays) {
                return left.windowDays - right.windowDays;
            }

            if (left.calculationTypeValue === right.calculationTypeValue) {
                return 0;
            }

            return left.calculationTypeValue === 'net' ? -1 : 1;
        });
    }

    @computed public get visibleItemGroups(): Array<{
        windowDays: number;
        periodLabel: string;
        items: Array<DefaultDepositLimitItemState>;
    }> {
        const groups: Array<{
            windowDays: number;
            periodLabel: string;
            items: Array<DefaultDepositLimitItemState>;
        }> = [];

        this.visibleItems.forEach((item) => {
            const lastGroup = groups[groups.length - 1];

            if (lastGroup !== undefined && lastGroup.windowDays === item.windowDays) {
                lastGroup.items.push(item);
                return;
            }

            groups.push({
                windowDays: item.windowDays,
                periodLabel: periodLabel(item.windowDays, item.displayLimitType),
                items: [item],
            });
        });

        groups.forEach((group) => {
            const labels = Array.from(
                new Set(group.items.map((item) => periodLabel(item.windowDays, item.displayLimitType)))
            );
            group.periodLabel = labels.join(' / ');
        });

        return groups;
    }

    @computed public get itemCountByWindow(): Map<number, number> {
        const counts = new Map<number, number>();

        this.items.forEach((item) => {
            counts.set(item.windowDays, (counts.get(item.windowDays) ?? 0) + 1);
        });

        return counts;
    }

    public isWindowCalculationLocked = (windowDays: number): boolean => {
        return isCustomDepositLimitWindow(windowDays) || (this.itemCountByWindow.get(windowDays) ?? 0) >= 2;
    };

    @computed public get hasValidationErrors(): boolean {
        return this.items.some((item) => item.hasError);
    }

    @computed public get customWindowDaysErrors(): Array<string> {
        return this.customWindowDays.errorsForView;
    }

    @computed public get customAmountErrors(): Array<string> {
        return this.customAmount.errorsForView;
    }

    @computed public get customAmountOverErrors(): Array<string> {
        return this.customAmountOver.errorsForView;
    }

    @action public addCustomNetRollingItem = async (): Promise<void> => {
        this.customWindowDays.setAsVisited();
        this.customAmount.setAsVisited();

        if (this.customSplitByAge) {
            this.customAmountOver.setAsVisited();
        }

        this.addErrorMessage = null;
        this.errorMessage = null;

        const customWindowDaysResult = this.customWindowDays.result.value;
        if (
            customWindowDaysResult.type === 'error' ||
            this.customAmount.result.value.type === 'error' ||
            (this.customSplitByAge && this.customAmountOver.result.value.type === 'error')
        ) {
            return;
        }

        const windowDays = customWindowDaysResult.data;

        if (this.items.some((item) => item.windowDays === windowDays && item.calculationTypeValue === 'net')) {
            this.addErrorMessage = 'This period already has this calculation type.';
            return;
        }

        const amountPounds = this.customAmount.value.trim();
        const amountOverPounds = this.customSplitByAge ? this.customAmountOver.value.trim() : amountPounds;
        const saved = await this.persistNewLimit({
            windowDays,
            calculationType: 'net',
            limitType: 'rolling',
            amountPounds,
            amountOverPounds,
            splitByAge: this.customSplitByAge,
        });

        if (saved === false) {
            return;
        }

        runInAction(() => {
            this.customWindowDays.reset();
            this.customAmount.reset();
            this.customAmountOver.reset();
            this.customSplitByAge = false;
        });
    };

    private persistNewLimit = async ({
        windowDays,
        calculationType,
        limitType,
        amountPounds,
        amountOverPounds,
        splitByAge,
    }: {
        windowDays: number;
        calculationType: DepositLimitConfigCalculationTypeType;
        limitType: DefaultDepositLimitGlobalType;
        amountPounds: string;
        amountOverPounds: string;
        splitByAge: boolean;
    }): Promise<boolean> => {
        const existing = this.existingByWindowAndCalculation.get(limitConfigKey(windowDays, calculationType)) ?? null;
        const item = new DefaultDepositLimitItemState(
            `new-${windowDays}-${calculationType}`,
            windowDays,
            amountPounds,
            amountOverPounds,
            calculationType,
            limitType,
            true,
            false,
            false,
            splitByAge,
            existing
        );

        this.isSaving = true;

        try {
            const payload = this.buildRequest(windowDays, item);
            const trpc = this.appState.common.trpcClient.client.responsibleGamblingRouter;
            const method = existing === null ? 'POST' : 'PUT';
            console.info(
                `[default-deposit-limits] ${method} window=${windowDays} existing=${existing !== null}\nbody=${JSON.stringify(payload, null, 2)}`
            );
            const response =
                existing === null
                    ? await trpc.createDepositLimitConfig.mutate(payload)
                    : await trpc.updateDepositLimitConfig.mutate({
                          windowLengthInDays: existing.windowLengthInDays,
                          limitType: existing.limitType,
                          calculationType: existing.calculationType,
                          payload,
                      });

            if (response.status !== 200) {
                runInAction(() => {
                    this.addErrorMessage = 'Failed to add default deposit limit.';
                });
                return false;
            }

            ToasterState.get(this.appState.common).setToast({
                variant: 'success',
                title: 'Success',
                message: 'Default deposit limit added.',
            });

            await this.loadConfiguration({ silent: true });
            return true;
        } catch (error) {
            console.error('[default-deposit-limits] addItem failed', error);
            runInAction(() => {
                this.addErrorMessage = 'Failed to add default deposit limit.';
            });
            return false;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    @action public loadConfiguration = async (options?: { silent?: boolean }): Promise<void> => {
        const silent = options?.silent === true;

        if (!silent) {
            this.isLoading = true;
        }
        this.errorMessage = null;

        try {
            const response =
                await this.appState.common.trpcClient.client.responsibleGamblingRouter.getDepositLimitConfig.query();

            if (response.status !== 200) {
                console.error(
                    `[default-deposit-limits] GET status=${response.status}\nresponse=${JSON.stringify(response, null, 2)}`
                );
                runInAction(() => {
                    this.errorMessage = 'Failed to load default deposit limits.';
                });
                return;
            }

            console.info(
                `[default-deposit-limits] GET status=${response.status} count=${response.body.length}\nresponse=${JSON.stringify(response.body, null, 2)}`
            );

            runInAction(() => {
                const existingByWindowAndCalculation = new Map<string, DepositLimitConfigResponseType>();

                response.body.forEach((record) => {
                    existingByWindowAndCalculation.set(
                        limitConfigKey(record.windowLengthInDays, record.calculationType),
                        record
                    );
                });

                this.existingByWindowAndCalculation = existingByWindowAndCalculation;
                this.items = response.body
                    .slice()
                    .sort((left, right) => left.windowLengthInDays - right.windowLengthInDays)
                    .map(
                        (record, index) =>
                            new DefaultDepositLimitItemState(
                                `${record.windowLengthInDays}-${record.limitType}-${record.calculationType}-${index}`,
                                record.windowLengthInDays,
                                penceToPoundsString(record.maxLimitUnderThreshold),
                                penceToPoundsString(record.maxLimitOverThreshold),
                                record.calculationType,
                                toGlobalLimitType(record.limitType),
                                record.limitEnabled,
                                record.hardLimit,
                                record.sendEmail,
                                record.maxLimitUnderThreshold !== record.maxLimitOverThreshold,
                                record
                            )
                    );
            });
        } catch (error) {
            console.error('[default-deposit-limits] GET failed', error);
            runInAction(() => {
                this.errorMessage = 'Failed to load default deposit limits.';
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @action public saveConfiguration = async (): Promise<void> => {
        this.items.forEach((item) => {
            if (item.limitEnabled) {
                item.amount.setAsVisited();

                if (item.splitByAge) {
                    item.amountOver.setAsVisited();
                }
            }
        });

        if (this.hasValidationErrors) {
            this.errorMessage = 'Please fix the highlighted fields before saving.';
            return;
        }

        this.isSaving = true;
        this.errorMessage = null;

        try {
            const ok = await this.persistAllItems();

            if (ok === false) {
                runInAction(() => {
                    this.errorMessage = 'Failed to save default deposit limits.';
                });
                return;
            }

            ToasterState.get(this.appState.common).setToast({
                variant: 'success',
                title: 'Success',
                message: 'Default deposit limits saved.',
            });

            await this.loadConfiguration({ silent: true });
        } catch {
            runInAction(() => {
                this.errorMessage = 'Failed to save default deposit limits.';
            });
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    private persistAllItems = async (): Promise<boolean> => {
        const trpc = this.appState.common.trpcClient.client.responsibleGamblingRouter;

        const results = await Promise.all(
            this.items.map(async (item) => {
                const payload = this.buildRequest(item.windowDays, item);
                const existing =
                    item.existing ??
                    this.existingByWindowAndCalculation.get(
                        limitConfigKey(item.windowDays, item.calculationTypeValue)
                    ) ??
                    null;

                const method = existing === null ? 'POST' : 'PUT';

                try {
                    const response =
                        existing === null
                            ? await trpc.createDepositLimitConfig.mutate(payload)
                            : await trpc.updateDepositLimitConfig.mutate({
                                  windowLengthInDays: existing.windowLengthInDays,
                                  limitType: existing.limitType,
                                  calculationType: existing.calculationType,
                                  payload,
                              });

                    return response.status === 200 ? 'ok' : 'error';
                } catch (error) {
                    console.error(`[default-deposit-limits] ${method} window=${item.windowDays} failed`, error);
                    return 'error';
                }
            })
        );

        return results.every((result) => result === 'ok');
    };

    private buildRequest = (
        windowDays: number,
        item: DefaultDepositLimitItemState | null
    ): DepositLimitConfigRequestType => {
        const existing =
            item === null
                ? null
                : (item.existing ??
                  this.existingByWindowAndCalculation.get(limitConfigKey(windowDays, item.calculationTypeValue)) ??
                  null);
        const enabled = item?.limitEnabled === true;
        const underPence = item !== null && item.amount.result.value.type === 'ok' ? item.amount.result.value.data : 0;
        const overFromField =
            item !== null && item.amountOver.result.value.type === 'ok' ? item.amountOver.result.value.data : 0;
        const overPence = item?.splitByAge === true ? overFromField : underPence;

        return {
            limitType: item === null ? (existing?.limitType ?? this.universeLimitType) : item.persistLimitType,
            calculationType: item === null ? (existing?.calculationType ?? 'net') : item.calculationTypeValue,
            windowLengthInDays: windowDays,
            maxLimitUnderThreshold:
                item !== null && underPence > 0 ? underPence : (existing?.maxLimitUnderThreshold ?? 0),
            maxLimitOverThreshold: item !== null && overPence > 0 ? overPence : (existing?.maxLimitOverThreshold ?? 0),
            sendEmail: item === null ? (existing?.sendEmail ?? false) : item.canSendEmail && item.sendEmail,
            sendEmailPauseDurationInDays: existing?.sendEmailPauseDurationInDays ?? DEFAULT_EMAIL_PAUSE_DAYS,
            hardLimit: item?.hardLimit ?? existing?.hardLimit ?? false,
            skipPendingState: existing?.skipPendingState ?? false,
            limitEnabled: enabled,
        };
    };
}
